/**
 * The web layer's half of Google Play Billing.
 *
 * Shape of the flow, and the ordering is the whole design:
 *
 *   config from OUR server  ->  Play sheet  ->  token to OUR server  ->  Google verifies  ->
 *   entitlement granted by the SAME code the web webhook uses  ->  only then, consume
 *
 * Nothing here decides an entitlement. The device reports what it thinks happened; the server asks
 * Google what actually happened. That separation is why an Android purchase and a web purchase end
 * up in one place rather than two, and why a rooted phone cannot mint a Studio plan.
 *
 * Every function is safe to call on the web: the plugin is absent there, `available()` is false,
 * and nothing else runs. The web product does not change because this file exists.
 */

import { Capacitor, registerPlugin } from "@capacitor/core";
import { authHeader } from "@/api/authToken";

interface PlayProductInfo {
  productId: string;
  title?: string;
  description?: string;
  price?: string;
  currency?: string;
  offerToken?: string;
}

interface PlayPurchase {
  productId: string;
  purchaseToken: string;
  acknowledged: boolean;
}

interface NuvraPlayBillingPlugin {
  isAvailable(): Promise<{ available: boolean; reason?: string }>;
  getProducts(o: { productIds: string[]; type: "subs" | "inapp" }): Promise<{ products: PlayProductInfo[] }>;
  purchase(o: { productId: string; offerToken?: string; obfuscatedAccountId?: string }):
    Promise<{ cancelled: boolean; purchases?: PlayPurchase[] }>;
  queryPurchases(o: { type: "subs" | "inapp" }): Promise<{ purchases: PlayPurchase[] }>;
  consume(o: { purchaseToken: string }): Promise<{ consumed: boolean }>;
  acknowledge(o: { purchaseToken: string }): Promise<{ acknowledged: boolean }>;
}

const PlayBilling = registerPlugin<NuvraPlayBillingPlugin>("NuvraPlayBilling");

export interface PlayConfig {
  verificationConfigured: boolean;
  obfuscatedAccountId: string;
  subscriptions: Array<{ productId: string; tier: "starter" | "pro" | "studio" }>;
  credits: Array<{ productId: string; acuAmount: number }>;
}

export interface PlayGrantResult {
  status: "granted" | "not_entitled" | "refused";
  kind?: "subscription" | "credits";
  tier?: string;
  acuAmount?: number;
  consumeRequired?: boolean;
  reason?: string;
}

/** True only where the native plugin is really present. */
export function playBillingAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("NuvraPlayBilling");
}

async function api<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: body === undefined ? "GET" : "POST",
    headers: { "Content-Type": "application/json", ...(await authHeader()) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let message = "Google Play purchases are unavailable right now.";
    try { const b = await res.json(); if (b?.error) message = String(b.error); } catch { /* keep default */ }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/** Product ids and the account binding, from OUR server. Never hardcoded in the app bundle. */
export function fetchPlayConfig(): Promise<PlayConfig> {
  return api<PlayConfig>("/api/billing/play/config");
}

/** Prices as Play formats them, in the customer's own currency. Never our own USD strings. */
export async function loadPlayProducts(productIds: string[], type: "subs" | "inapp"): Promise<PlayProductInfo[]> {
  if (!playBillingAvailable() || productIds.length === 0) return [];
  const { products } = await PlayBilling.getProducts({ productIds, type });
  return products;
}

async function verify(purchase: PlayPurchase): Promise<PlayGrantResult> {
  const result = await api<PlayGrantResult>("/api/billing/play/verify", {
    productId: purchase.productId,
    purchaseToken: purchase.purchaseToken,
  });
  // Consumption LAST, and only on the server's say-so. Doing it earlier destroys the only proof
  // the purchase happened, so a grant that then failed would leave the customer paid and empty
  // handed with nothing left to retry against.
  if (result.status === "granted" && result.consumeRequired) {
    await PlayBilling.consume({ purchaseToken: purchase.purchaseToken }).catch(() => {
      // The credits are already in the balance. A failed consume only blocks re-buying the same
      // pack, and the next launch's sync retries it.
    });
  }
  return result;
}

/**
 * Buy a plan or a credit pack.
 *
 * The returned status is the SERVER's, not the device's, so a caller cannot mistake "Play said OK"
 * for "the plan is active".
 */
export async function purchaseWithPlay(params: {
  productId: string;
  type: "subs" | "inapp";
  config: PlayConfig;
}): Promise<PlayGrantResult | { status: "cancelled" }> {
  if (!playBillingAvailable()) throw new Error("Google Play purchases are not available here.");
  if (!params.config.verificationConfigured) {
    // Refusing to sell is the right call. Taking the money and being unable to verify it leaves a
    // customer charged and unentitled, which is worse than an unavailable purchase button.
    throw new Error("Purchases are temporarily unavailable. Please try again shortly.");
  }

  const products = await loadPlayProducts([params.productId], params.type);
  const details = products.find(p => p.productId === params.productId);
  if (!details) throw new Error("That plan is not available on this device.");

  const outcome = await PlayBilling.purchase({
    productId: params.productId,
    offerToken: details.offerToken,
    obfuscatedAccountId: params.config.obfuscatedAccountId,
  });
  if (outcome.cancelled) return { status: "cancelled" };

  const purchase = outcome.purchases?.[0];
  if (!purchase) throw new Error("The purchase did not complete.");
  return verify(purchase);
}

/**
 * Re-send everything Play still considers owned.
 *
 * The recovery path for a purchase that completed on the device but never reached the server: the
 * process was killed, the network dropped, the app was backgrounded mid-request. Called on launch,
 * it is what makes those cases self-heal instead of turning into a support ticket. Failures are
 * swallowed on purpose, because this runs in the background and must never interrupt a launch.
 */
export async function syncPlayPurchases(): Promise<void> {
  if (!playBillingAvailable()) return;
  try {
    const [subs, inapp] = await Promise.all([
      PlayBilling.queryPurchases({ type: "subs" }),
      PlayBilling.queryPurchases({ type: "inapp" }),
    ]);
    const purchases = [...subs.purchases, ...inapp.purchases];
    if (purchases.length === 0) return;

    const { results } = await api<{ results: Array<PlayGrantResult & { productId: string }> }>(
      "/api/billing/play/sync",
      { purchases: purchases.map(p => ({ productId: p.productId, purchaseToken: p.purchaseToken })) },
    );

    for (const r of results) {
      if (r.status !== "granted" || !r.consumeRequired) continue;
      const match = purchases.find(p => p.productId === r.productId);
      if (match) await PlayBilling.consume({ purchaseToken: match.purchaseToken }).catch(() => {});
    }
  } catch {
    // Offline, signed out, or the server is unreachable. The next launch tries again.
  }
}
